import { useQuery } from '@apollo/client'
import { gql } from '~/__generated__'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/ui/card'
import { Badge } from '~/ui/badge'
import { Skeleton } from '~/ui/skeleton'
import { useCurrentOrganization } from '~/auth/use-current-organization'
import {
  Clock,
  Music,
  DollarSign,
  MapPin,
  PlayCircle,
  Wifi,
  WifiOff,
  Calendar,
  Activity
} from 'lucide-react'

const DASHBOARD_QUERY = gql(`
  query AdminDashboard($organizationId: ID!) {
    playbackAnalytics(organizationId: $organizationId, days: 30) {
      totalListeningTimeHours
      totalTracksPlayed
      averageTrackCompletionPercentage
      topTracks {
        track {
          id
          name
          artists {
            name
          }
        }
        playCount
      }
    }
    organizationInvoices(organizationId: $organizationId, limit: 100) {
      id
      amount
      createdAt
      status
    }
    organizationSubscriptions(organizationId: $organizationId, first: 50) {
      nodes {
        id
        status
        quantity
        billingPlan {
          name
          monthlyBasePriceCents
        }
        zone {
          id
          name
        }
      }
    }
    zones(organizationId: $organizationId, first: 50) {
      nodes {
        id
        name
        location {
          id
          name
        }
        devices {
          id
          code
          status
        }
        schedules {
          id
          playlist {
            id
            name
          }
          startTime
          endTime
          rrule
        }
      }
    }
  }
`)

const ZONE_STATUS_QUERY = gql(`
  query ZoneStatus($zoneId: ID!) {
    zoneStatus(zoneId: $zoneId) {
      playing
      currentTrack {
        id
        name
        artists {
          name
        }
      }
      formattedPosition
      formattedDuration
      volume
    }
  }
`)

const isOnlineStatus = (status: string) => ['online', 'playing', 'paused'].includes(status)

export const AdminDashboardScreen = () => {
  const { id: organizationId } = useCurrentOrganization()

  const { data, loading, error } = useQuery(DASHBOARD_QUERY, {
    variables: { organizationId },
    skip: !organizationId,
    pollInterval: 30000, // Refresh every 30 seconds
  })

  if (loading) {
    return <DashboardSkeleton />
  }

  if (error) {
    return (
      <div className="flex flex-1 flex-col p-7">
        <h1 className="text-2xl">Dashboard</h1>
        <div className="mt-4 p-4 border border-red-200 bg-red-50 rounded-lg">
          <p className="text-red-800">Error loading dashboard data: {error.message}</p>
        </div>
      </div>
    )
  }

  const analytics = data?.playbackAnalytics
  const invoices = data?.organizationInvoices || []
  const zones = data?.zones?.nodes || []

  const monthlyRevenue = invoices
    .filter((invoice: any) => {
      const invoiceDate = new Date(invoice.createdAt)
      const now = new Date()
      return invoice.status === 'paid' &&
             invoiceDate.getMonth() === now.getMonth() &&
             invoiceDate.getFullYear() === now.getFullYear()
    })
    .reduce((sum: number, invoice: any) => sum + (invoice.amount || 0), 0) / 100

  const activeZones = zones.filter((zone: any) =>
    zone.devices.some((device: any) => isOnlineStatus(device.status))
  ).length
  const totalDevices = zones.reduce((sum: number, zone: any) => sum + zone.devices.length, 0)
  const onlineDevices = zones.reduce((sum: number, zone: any) =>
    sum + zone.devices.filter((device: any) => isOnlineStatus(device.status)).length, 0
  )

  return (
    <div className="flex flex-1 flex-col p-7 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground mt-2">
          Overview of your organization's music streaming environment
        </p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Listening Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.totalListeningTimeHours?.toFixed(1) || 0}h
            </div>
            <p className="text-xs text-muted-foreground">
              Last 30 days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tracks Played</CardTitle>
            <Music className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.totalTracksPlayed || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Last 30 days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${monthlyRevenue.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">
              This month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Zones</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {activeZones}/{zones.length}
            </div>
            <p className="text-xs text-muted-foreground">
              {onlineDevices}/{totalDevices} devices online
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Zones Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Zones Overview
          </CardTitle>
          <CardDescription>
            Current status of all zones in your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {zones.map((zone) => (
              <ZoneRow key={zone.id} zone={zone} />
            ))}
            {zones.length === 0 && (
              <p className="text-muted-foreground text-center py-8">
                No zones configured yet. Create your first zone to get started.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Top Tracks */}
      {analytics?.topTracks && analytics.topTracks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PlayCircle className="h-5 w-5" />
              Top Tracks
            </CardTitle>
            <CardDescription>
              Most played tracks in the last 30 days
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.topTracks.slice(0, 5).map((item: any, index: number) => (
                <div key={item.track.id} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{item.track.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {item.track.artists.map((a: any) => a.name).join(', ')}
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="flex-shrink-0">{item.playCount} plays</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

const ZoneRow = ({ zone }: { zone: any }) => {
  const onlineDevices = zone.devices.filter((device: any) => isOnlineStatus(device.status))
  const { data: statusData } = useQuery(ZONE_STATUS_QUERY, {
    variables: { zoneId: zone.id },
    pollInterval: 10000, // Refresh every 10 seconds
  })

  const status = statusData?.zoneStatus
  // Find schedule that matches current time (simplified - just show first schedule)
  const activeSchedule = zone.schedules.length > 0 ? zone.schedules[0] : null

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <h3 className="font-medium truncate">{zone.name}</h3>
          <Badge variant={onlineDevices.length > 0 ? "default" : "secondary"} className="w-fit">
            {onlineDevices.length > 0 ? (
              <>
                <Wifi className="w-3 h-3 mr-1" />
                {onlineDevices.length} online
              </>
            ) : (
              <>
                <WifiOff className="w-3 h-3 mr-1" />
                Offline
              </>
            )}
          </Badge>
        </div>

        {zone.location && (
          <p className="text-sm text-muted-foreground mt-1">{zone.location.name}</p>
        )}

        {status?.currentTrack && (
          <div className="flex items-center gap-2 mt-2">
            <PlayCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
            <span className="text-sm truncate">
              {status.currentTrack.name} by {(status.currentTrack.artists ?? []).map((a: any) => a.name).join(', ')}
            </span>
            <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
              {status.formattedPosition} / {status.formattedDuration}
            </span>
          </div>
        )}

        {activeSchedule && (
          <div className="flex items-center gap-2 mt-1">
            <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <span className="text-sm text-muted-foreground truncate">
              Schedule: {activeSchedule.playlist.name} ({activeSchedule.startTime} - {activeSchedule.endTime})
            </span>
          </div>
        )}
      </div>

      <div className="flex sm:flex-col sm:text-right gap-2 sm:gap-0">
        <p className="text-sm text-muted-foreground">
          {zone.devices.length} device{zone.devices.length !== 1 ? 's' : ''}
        </p>
        <p className="text-sm text-muted-foreground">
          {zone.schedules.length} schedule{zone.schedules.length !== 1 ? 's' : ''}
        </p>
      </div>
    </div>
  )
}

const DashboardSkeleton = () => (
  <div className="flex flex-1 flex-col p-7 space-y-6">
    <div>
      <Skeleton className="h-9 w-48 mb-2" />
      <Skeleton className="h-5 w-96" />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[...Array(4)].map((_, i) => (
        <Card key={i}>
          <CardHeader className="pb-2">
            <Skeleton className="h-4 w-24" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-8 w-16 mb-2" />
            <Skeleton className="h-3 w-20" />
          </CardContent>
        </Card>
      ))}
    </div>

    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-48" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
)
